import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart3, FileText, Plus, Upload, Loader2, CheckCircle2,
  AlertCircle, Clock, Star, ChevronRight, Settings, Target,
  User, UserCheck, Bot, X, Trash2, Edit2, Eye
} from "lucide-react";
import { FALLBACK_PERFORMANCE_CONTRACTS, FALLBACK_PENDING_REVIEWS } from "@/data/dummyData";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Rating Scale Component
function RatingScale({ value, onChange, disabled = false }) {
  const ratings = [
    { value: 1, label: "Very Poor", color: "bg-red-500" },
    { value: 2, label: "Below Average", color: "bg-orange-500" },
    { value: 3, label: "Met Expectations", color: "bg-yellow-500" },
    { value: 4, label: "Above Expectations", color: "bg-emerald-500" },
    { value: 5, label: "Excellent", color: "bg-blue-500" }
  ];

  return (
    <div className="flex items-center gap-1">
      {ratings.map((rating) => (
        <button
          key={rating.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange?.(rating.value)}
          className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold transition-all ${
            value === rating.value 
              ? `${rating.color} text-white scale-110` 
              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
          } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          title={rating.label}
        >
          {rating.value}
        </button>
      ))}
      {value && (
        <span className="ml-2 text-xs text-slate-500">
          {ratings.find(r => r.value === value)?.label}
        </span>
      )}
    </div>
  );
}

// KPA Editor Component
function KPAEditor({ kpas, setKpas, maxKpas = 5, maxWeight = 40 }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newKpa, setNewKpa] = useState({ title: "", description: "", kpis: "", weight: 20 });

  const totalWeight = kpas.reduce((sum, k) => sum + (k.weight || 0), 0);
  const remainingWeight = 100 - totalWeight;

  const addKpa = () => {
    if (!newKpa.title.trim()) {
      toast.error("KPA title is required");
      return;
    }
    if (kpas.length >= maxKpas) {
      toast.error(`Maximum ${maxKpas} KPAs allowed`);
      return;
    }
    if (newKpa.weight > maxWeight) {
      toast.error(`Maximum weight per KPA is ${maxWeight}%`);
      return;
    }
    if (totalWeight + newKpa.weight > 100) {
      toast.error(`Total weight exceeds 100%. Remaining: ${remainingWeight}%`);
      return;
    }

    setKpas([...kpas, {
      ...newKpa,
      kpis: newKpa.kpis.split(",").map(k => k.trim()).filter(Boolean)
    }]);
    setNewKpa({ title: "", description: "", kpis: "", weight: 20 });
    setShowAdd(false);
  };

  const removeKpa = (index) => {
    setKpas(kpas.filter((_, i) => i !== index));
  };

  const updateKpaWeight = (index, weight) => {
    const updated = [...kpas];
    updated[index].weight = weight;
    setKpas(updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-slate-800">Key Performance Areas (KPAs)</h4>
          <p className="text-xs text-slate-500">
            {kpas.length}/{maxKpas} KPAs | Total Weight: {totalWeight}% 
            {totalWeight !== 100 && <span className="text-orange-500"> (must equal 100%)</span>}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="rounded-none"
          onClick={() => setShowAdd(true)}
          disabled={kpas.length >= maxKpas}
        >
          <Plus className="w-4 h-4 mr-1" /> Add KPA
        </Button>
      </div>

      <Progress value={totalWeight} className="h-2" />

      <div className="space-y-3">
        {kpas.map((kpa, index) => (
          <div key={index} className="border border-slate-200 p-4 bg-app">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-500" />
                  <span className="font-medium text-slate-800">{kpa.title}</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">
                    {kpa.weight}%
                  </span>
                </div>
                {kpa.description && (
                  <p className="text-sm text-slate-600 mt-1 ml-6">{kpa.description}</p>
                )}
                {kpa.kpis?.length > 0 && (
                  <div className="ml-6 mt-2 flex flex-wrap gap-1">
                    {kpa.kpis.map((kpi, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs">
                        {kpi}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={maxWeight}
                  value={kpa.weight}
                  onChange={(e) => updateKpaWeight(index, parseInt(e.target.value) || 0)}
                  className="w-16 h-8 text-xs rounded-none"
                />
                <button 
                  onClick={() => removeKpa(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add KPA Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Key Performance Area</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>KPA Title</Label>
              <Input
                value={newKpa.title}
                onChange={(e) => setNewKpa(k => ({ ...k, title: e.target.value }))}
                placeholder="e.g. Financial Management"
                className="rounded-none"
              />
            </div>
            <div>
              <Label>Description/Objectives</Label>
              <Textarea
                value={newKpa.description}
                onChange={(e) => setNewKpa(k => ({ ...k, description: e.target.value }))}
                placeholder="Describe the objectives and expectations..."
                className="rounded-none"
              />
            </div>
            <div>
              <Label>Key Performance Indicators (comma-separated)</Label>
              <Input
                value={newKpa.kpis}
                onChange={(e) => setNewKpa(k => ({ ...k, kpis: e.target.value }))}
                placeholder="e.g. Budget variance < 5%, Monthly reports"
                className="rounded-none"
              />
            </div>
            <div>
              <Label>Weight (%) - Max {maxWeight}%</Label>
              <Input
                type="number"
                min={1}
                max={Math.min(maxWeight, remainingWeight)}
                value={newKpa.weight}
                onChange={(e) => setNewKpa(k => ({ ...k, weight: parseInt(e.target.value) || 0 }))}
                className="rounded-none"
              />
              <p className="text-xs text-slate-500 mt-1">Remaining: {remainingWeight}%</p>
            </div>
            <Button onClick={addKpa} className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500">
              Add KPA
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PerformanceApp() {
  // State
  const [settings, setSettings] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dialog states
  const [showCreateContract, setShowCreateContract] = useState(false);
  const [showUploadContract, setShowUploadContract] = useState(false);
  const [showSelfAssessment, setShowSelfAssessment] = useState(false);
  const [showSupervisorReview, setShowSupervisorReview] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAssessmentDetails, setShowAssessmentDetails] = useState(false);

  // Form states
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [newContractKpas, setNewContractKpas] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selfAssessmentData, setSelfAssessmentData] = useState([]);
  const [supervisorRatings, setSupervisorRatings] = useState([]);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [settingsRes, contractsRes, periodsRes, profileRes, pendingRes] = await Promise.all([
        axios.get(`${API}/api/hr/performance/settings`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/performance/contracts`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/performance/periods`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/api/hr/my-profile`, { headers: getHeaders(), withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/api/hr/performance/pending-reviews`, { headers: getHeaders(), withCredentials: true })
      ]);

      setSettings(settingsRes.data);
      const cData = contractsRes.data || [];
      setContracts(cData.length > 0 ? cData : FALLBACK_PERFORMANCE_CONTRACTS);
      setPeriods(periodsRes.data.periods || []);
      setMyProfile(profileRes.data);
      const pData = pendingRes.data || [];
      setPendingReviews(pData.length > 0 ? pData : FALLBACK_PENDING_REVIEWS);
    } catch (err) {
      setContracts(FALLBACK_PERFORMANCE_CONTRACTS);
      setPendingReviews(FALLBACK_PENDING_REVIEWS);
      toast.error("Failed to load performance data. Showing demo data.");
    }
    setIsLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setIsExtracting(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${API}/api/hr/performance/contracts/extract-kpas`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true
      });

      if (res.data.kpas?.length > 0) {
        setNewContractKpas(res.data.kpas);
        toast.success(`Extracted ${res.data.kpas.length} KPAs from document`);
        setShowUploadContract(false);
        setShowCreateContract(true);
      } else {
        toast.error("No KPAs could be extracted from the document");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extract KPAs");
    }
    setIsExtracting(false);
  };

  const createContract = async () => {
    if (!myProfile) {
      toast.error("Your member profile not found. Please contact HR.");
      return;
    }
    if (!selectedPeriod) {
      toast.error("Please select an assessment period");
      return;
    }
    if (newContractKpas.length === 0) {
      toast.error("Please add at least one KPA");
      return;
    }

    const totalWeight = newContractKpas.reduce((sum, k) => sum + (k.weight || 0), 0);
    if (totalWeight !== 100) {
      toast.error("Total KPA weight must equal 100%");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/hr/performance/contracts`, {
        member_id: myProfile.id,
        period: selectedPeriod,
        kpas: newContractKpas
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      toast.success("Performance contract created");
      setShowCreateContract(false);
      setNewContractKpas([]);
      setSelectedPeriod("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create contract");
    }
    setIsSubmitting(false);
  };

  const startSelfAssessment = (contract) => {
    setSelectedContract(contract);
    setSelfAssessmentData(contract.kpas.map(kpa => ({
      kpa_title: kpa.title,
      achievements: "",
      self_rating: 3,
      weight: kpa.weight
    })));
    setShowSelfAssessment(true);
  };

  const submitSelfAssessment = async () => {
    if (!selectedContract) return;

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/hr/performance/assessments`, {
        contract_id: selectedContract.id,
        assessments: selfAssessmentData
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      toast.success("Self-assessment submitted. AI is now generating ratings...");
      setShowSelfAssessment(false);
      setSelfAssessmentData([]);
      setSelectedContract(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit assessment");
    }
    setIsSubmitting(false);
  };

  const startSupervisorReview = (assessment) => {
    setSelectedAssessment(assessment);
    setSupervisorRatings(assessment.assessments.map((a, i) => ({
      kpa_title: a.kpa_title,
      rating: assessment.ai_ratings?.[i]?.ai_rating || 3,
      notes: ""
    })));
    setShowSupervisorReview(true);
  };

  const submitSupervisorReview = async () => {
    if (!selectedAssessment) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/hr/performance/assessments/${selectedAssessment.id}/supervisor-review`, {
        ratings: supervisorRatings
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      toast.success("Supervisor review submitted");
      setShowSupervisorReview(false);
      setSupervisorRatings([]);
      setSelectedAssessment(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    }
    setIsSubmitting(false);
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/api/hr/performance/settings`, newSettings, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      setSettings(newSettings);
      toast.success("Settings updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    }
  };

  const viewAssessmentDetails = async (contractId) => {
    try {
      // Find assessment for this contract
      const assessmentRes = await axios.get(`${API}/api/hr/performance/contracts/${contractId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setSelectedAssessment(assessmentRes.data);
      setShowAssessmentDetails(true);
    } catch (err) {
      toast.error("Failed to load assessment details");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: "bg-slate-200 text-slate-700", icon: Edit2, label: "Draft" },
      pending_supervisor: { color: "bg-amber-100 text-amber-700", icon: Clock, label: "Pending Review" },
      completed: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Completed" }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${badge.color}`}>
        <badge.icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
                PERFORMANCE APP
              </h1>
              <p className="text-slate-600">KPA-Based Performance Assessment System</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="rounded-none"
              onClick={() => setShowSettings(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              variant="outline" 
              className="rounded-none"
              onClick={() => setShowUploadContract(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Contract
            </Button>
            <Button 
              className="rounded-none bg-purple-600 hover:bg-purple-500"
              onClick={() => setShowCreateContract(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Contract
            </Button>
          </div>
        </div>

        {/* Settings Summary */}
        {settings && (
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-blue-500" />
              <span>AI: {settings.ai_weight}%</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-emerald-500" />
              <span>Self: {settings.member_weight}%</span>
            </div>
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-purple-500" />
              <span>Supervisor: {settings.supervisor_weight}%</span>
            </div>
            <span className="text-slate-400">|</span>
            <span>Max {settings.max_kpas} KPAs, {settings.max_kpa_weight}% max weight each</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="my-contracts">
            <TabsList className="bg-slate-100 rounded-none">
              <TabsTrigger value="my-contracts" className="rounded-none" data-testid="tab-my-contracts">
                <FileText className="w-4 h-4 mr-2" />
                My Contracts
              </TabsTrigger>
              <TabsTrigger value="pending-reviews" className="rounded-none" data-testid="tab-pending-reviews">
                <Clock className="w-4 h-4 mr-2" />
                Pending Reviews ({pendingReviews.length})
              </TabsTrigger>
            </TabsList>

            {/* My Contracts Tab */}
            <TabsContent value="my-contracts">
              <div className="bg-white border border-slate-200 p-6">
                {contracts.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-700 mb-1">No Performance Contracts</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Create a new contract or upload an existing one to get started.
                    </p>
                    <Button 
                      className="rounded-none bg-purple-600 hover:bg-purple-500"
                      onClick={() => setShowCreateContract(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Contract
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contracts.map((contract) => (
                      <div 
                        key={contract.id} 
                        className="border border-slate-200 p-4 hover:border-purple-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-slate-800">{contract.period} {contract.year}</span>
                              {getStatusBadge(contract.status)}
                            </div>
                            <p className="text-sm text-slate-500">{contract.kpas?.length || 0} KPAs defined</p>
                          </div>
                          <div className="flex gap-2">
                            {contract.status === "draft" && (
                              <Button 
                                size="sm" 
                                className="rounded-none bg-emerald-600 hover:bg-emerald-500"
                                onClick={() => startSelfAssessment(contract)}
                              >
                                Self-Assess
                              </Button>
                            )}
                            {contract.status === "completed" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="rounded-none"
                                onClick={() => viewAssessmentDetails(contract.id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Results
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* KPA Summary */}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {contract.kpas?.map((kpa, i) => (
                            <span 
                              key={i} 
                              className="px-2 py-1 bg-slate-100 text-slate-600 text-xs"
                            >
                              {kpa.title} ({kpa.weight}%)
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Pending Reviews Tab (for Supervisors) */}
            <TabsContent value="pending-reviews">
              <div className="bg-white border border-slate-200 p-6">
                {pendingReviews.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-700 mb-1">All Caught Up!</h3>
                    <p className="text-sm text-slate-500">
                      No pending assessments require your review.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingReviews.map((assessment) => (
                      <div 
                        key={assessment.id} 
                        className="border border-amber-200 bg-amber-50 p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-800">
                              Assessment ID: {assessment.id.slice(0, 12)}...
                            </p>
                            <p className="text-sm text-slate-600 mt-1">
                              {assessment.assessments?.length || 0} KPAs to review
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Submitted: {new Date(assessment.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button 
                            className="rounded-none bg-purple-600 hover:bg-purple-500"
                            onClick={() => startSupervisorReview(assessment)}
                          >
                            Review Now
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Rating Guide */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" />
              Rating Guide
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-red-500 text-white rounded flex items-center justify-center text-xs font-bold">1</span>
                <span className="text-slate-600">Very Poor - Major negative impact</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-500 text-white rounded flex items-center justify-center text-xs font-bold">2</span>
                <span className="text-slate-600">Below Average - Did not meet expectations</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-yellow-500 text-white rounded flex items-center justify-center text-xs font-bold">3</span>
                <span className="text-slate-600">Met Expectations - Did what was expected</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-emerald-500 text-white rounded flex items-center justify-center text-xs font-bold">4</span>
                <span className="text-slate-600">Above Expectations - Exceeded requirements</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-500 text-white rounded flex items-center justify-center text-xs font-bold">5</span>
                <span className="text-slate-600">Excellent - Major positive org impact</span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-800 mb-3">Quick Stats</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-app">
                <p className="text-2xl font-bold text-purple-600">{contracts.length}</p>
                <p className="text-xs text-slate-500">Total Contracts</p>
              </div>
              <div className="text-center p-3 bg-app">
                <p className="text-2xl font-bold text-amber-600">{pendingReviews.length}</p>
                <p className="text-xs text-slate-500">Pending Reviews</p>
              </div>
              <div className="text-center p-3 bg-app">
                <p className="text-2xl font-bold text-emerald-600">
                  {contracts.filter(c => c.status === "completed").length}
                </p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
              <div className="text-center p-3 bg-app">
                <p className="text-2xl font-bold text-blue-600">
                  {contracts.filter(c => c.status === "draft").length}
                </p>
                <p className="text-xs text-slate-500">Draft</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Contract Dialog */}
      <Dialog open={showUploadContract} onOpenChange={setShowUploadContract}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Performance Contract</DialogTitle>
            <DialogDescription>
              Upload a PDF, DOCX, or TXT file containing KPAs. AI will extract them automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,.docx,.doc,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <div 
              className="border-2 border-dashed border-slate-300 p-8 text-center cursor-pointer hover:border-purple-400"
              onClick={() => fileInputRef.current?.click()}
            >
              {isExtracting ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-8 h-8 text-purple-500 animate-spin mb-2" />
                  <p className="text-sm text-slate-600">Extracting KPAs with AI...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Click to upload or drag and drop</p>
                  <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT supported</p>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Contract Dialog */}
      <Dialog open={showCreateContract} onOpenChange={setShowCreateContract}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Create Performance Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <Label>Assessment Period</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="rounded-none">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <KPAEditor 
              kpas={newContractKpas}
              setKpas={setNewContractKpas}
              maxKpas={settings?.max_kpas || 5}
              maxWeight={settings?.max_kpa_weight || 40}
            />

            <Button 
              onClick={createContract}
              disabled={isSubmitting || newContractKpas.length === 0}
              className="w-full rounded-none bg-purple-600 hover:bg-purple-500"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Contract"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Self-Assessment Dialog */}
      <Dialog open={showSelfAssessment} onOpenChange={setShowSelfAssessment}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Self-Assessment</DialogTitle>
            <DialogDescription>
              Rate your performance on each KPA and describe your achievements.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-6 pr-4">
              {selfAssessmentData.map((item, index) => (
                <div key={index} className="border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-purple-500" />
                      <span className="font-medium text-slate-800">{item.kpa_title}</span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs">
                        {item.weight}%
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">What did you achieve?</Label>
                      <Textarea
                        value={item.achievements}
                        onChange={(e) => {
                          const updated = [...selfAssessmentData];
                          updated[index].achievements = e.target.value;
                          setSelfAssessmentData(updated);
                        }}
                        placeholder="Describe your accomplishments, deliverables, and impact..."
                        className="rounded-none mt-1"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label className="text-sm">Your Self-Rating</Label>
                      <div className="mt-1">
                        <RatingScale
                          value={item.self_rating}
                          onChange={(rating) => {
                            const updated = [...selfAssessmentData];
                            updated[index].self_rating = rating;
                            setSelfAssessmentData(updated);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-none" onClick={() => setShowSelfAssessment(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitSelfAssessment}
              disabled={isSubmitting}
              className="rounded-none bg-emerald-600 hover:bg-emerald-500"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Assessment"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Supervisor Review Dialog */}
      <Dialog open={showSupervisorReview} onOpenChange={setShowSupervisorReview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Supervisor Review</DialogTitle>
            <DialogDescription>
              Review the self-assessment and AI recommendations, then provide your final ratings.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-6 pr-4">
              {selectedAssessment?.assessments?.map((item, index) => {
                const aiData = selectedAssessment.ai_ratings?.[index] || {};
                return (
                  <div key={index} className="border border-slate-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-purple-500" />
                        <span className="font-medium text-slate-800">{item.kpa_title}</span>
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs">
                          {item.weight}%
                        </span>
                      </div>
                    </div>

                    {/* Self-Assessment Summary */}
                    <div className="bg-emerald-50 p-3 mb-3">
                      <p className="text-xs text-emerald-700 font-medium mb-1">
                        <User className="w-3 h-3 inline mr-1" />
                        Self-Assessment (Rating: {item.self_rating}/5)
                      </p>
                      <p className="text-sm text-slate-700">{item.achievements}</p>
                    </div>

                    {/* AI Rating */}
                    <div className="bg-blue-50 p-3 mb-3">
                      <p className="text-xs text-blue-700 font-medium mb-1">
                        <Bot className="w-3 h-3 inline mr-1" />
                        AI Recommendation (Rating: {aiData.ai_rating || 3}/5)
                      </p>
                      {aiData.ai_justification && (
                        <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans">
                          {aiData.ai_justification}
                        </pre>
                      )}
                    </div>

                    {/* Supervisor Rating */}
                    <div className="space-y-2">
                      <Label className="text-sm">Your Rating</Label>
                      <RatingScale
                        value={supervisorRatings[index]?.rating}
                        onChange={(rating) => {
                          const updated = [...supervisorRatings];
                          updated[index].rating = rating;
                          setSupervisorRatings(updated);
                        }}
                      />
                      <Textarea
                        value={supervisorRatings[index]?.notes || ""}
                        onChange={(e) => {
                          const updated = [...supervisorRatings];
                          updated[index].notes = e.target.value;
                          setSupervisorRatings(updated);
                        }}
                        placeholder="Optional notes..."
                        className="rounded-none mt-1"
                        rows={2}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-none" onClick={() => setShowSupervisorReview(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitSupervisorReview}
              disabled={isSubmitting}
              className="rounded-none bg-purple-600 hover:bg-purple-500"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Final Review"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Performance Settings</DialogTitle>
            <DialogDescription>
              Configure the weighting for performance assessments.
            </DialogDescription>
          </DialogHeader>
          {settings && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">AI Weight (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={settings.ai_weight}
                    onChange={(e) => setSettings(s => ({ ...s, ai_weight: parseInt(e.target.value) || 0 }))}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs">Self Weight (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={settings.member_weight}
                    onChange={(e) => setSettings(s => ({ ...s, member_weight: parseInt(e.target.value) || 0 }))}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs">Supervisor (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={settings.supervisor_weight}
                    onChange={(e) => setSettings(s => ({ ...s, supervisor_weight: parseInt(e.target.value) || 0 }))}
                    className="rounded-none"
                  />
                </div>
              </div>
              <p className={`text-xs ${
                settings.ai_weight + settings.member_weight + settings.supervisor_weight === 100 
                  ? 'text-emerald-600' : 'text-red-500'
              }`}>
                Total: {settings.ai_weight + settings.member_weight + settings.supervisor_weight}% 
                (must equal 100%)
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Max KPAs</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={settings.max_kpas}
                    onChange={(e) => setSettings(s => ({ ...s, max_kpas: parseInt(e.target.value) || 5 }))}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max KPA Weight (%)</Label>
                  <Input
                    type="number"
                    min={10}
                    max={100}
                    value={settings.max_kpa_weight}
                    onChange={(e) => setSettings(s => ({ ...s, max_kpa_weight: parseInt(e.target.value) || 40 }))}
                    className="rounded-none"
                  />
                </div>
              </div>

              <Button 
                onClick={() => updateSettings(settings)}
                disabled={settings.ai_weight + settings.member_weight + settings.supervisor_weight !== 100}
                className="w-full rounded-none bg-purple-600 hover:bg-purple-500"
              >
                Save Settings
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assessment Details Dialog */}
      <Dialog open={showAssessmentDetails} onOpenChange={setShowAssessmentDetails}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Assessment Results</DialogTitle>
          </DialogHeader>
          {selectedAssessment && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-4">
                {/* Overall Score */}
                {selectedAssessment.final_ratings && (
                  <div className="bg-purple-50 p-4 text-center">
                    <p className="text-sm text-purple-700 mb-1">Overall Score</p>
                    <p className="text-4xl font-bold text-purple-600">
                      {selectedAssessment.final_ratings.overall_score?.toFixed(2) || "N/A"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">out of 5.0</p>
                  </div>
                )}

                {/* KPA Details */}
                {selectedAssessment.kpas?.map((kpa, i) => (
                  <div key={i} className="border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-800">{kpa.title}</span>
                      <span className="text-sm text-slate-500">{kpa.weight}%</span>
                    </div>
                    {kpa.description && (
                      <p className="text-sm text-slate-600 mt-1">{kpa.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
